import { UPDATE_OPS_REPORTING } from '@common/constants/webSockets'
import { OngoingReportingType, asOngoingReportingType } from '@common/interfaces/bbc'
import { IOverviewFeed } from '@common/interfaces/overview'
import {
  IOPSReporting,
  IOPSReportingFlow,
  IProspect,
  IProspectTerms,
  IProspectsAggregation,
  IProspectPipelineMetrics,
  IProspectReportingAggregation,
  IProspectSummaryInventory,
  IProspectSummaryCashFlow,
  IProspectARSummary,
  IProspectAPSummary,
  IProspectTermsSummaryInfo,
} from '@common/interfaces/prospects'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IAction } from '../apiMiddleware'
import { SUBMIT_ONGOING_REPORTING_SUCCESS } from '../ongoingReporting/actions'
import * as actions from './actions'

export default combineReducers({
  loadingArray: loadingArrayReducer(
    [actions.prefix],
    [
      actions.UPDATE_REPORTING_REQUEST,
      actions.LIST_REQUEST,
      actions.SHOW_REQUEST,
      actions.SHOW_FOLDERS_REQUEST,
      actions.UPDATE_REQUEST,
      actions.UPDATE_REPORTING_FLOW_ADDITIONAL_REQUEST,
    ],
  ),
  reporting(state: IOPSReporting | null = null, action: IAction): IOPSReporting | null {
    switch (action.type) {
      case actions.SHOW_SUCCESS:
      case actions.START_REPORTING_SUCCESS: {
        const data = action.data as IOPSReporting
        if (!state) {
          return data
        }

        return {
          ...state,
          ...data,
          clientInfo: {
            ...(state.clientInfo || {}),
            ...(data.clientInfo || {}),
          } as IProspect,
        }
      }
      case actions.UPDATE_REPORTING_SUCCESS:
      case actions.PULL_REPORTS_SUCCESS:
      case actions.UPDATE_SUCCESS:
      case actions.SHOW_FOLDERS_SUCCESS:
      case SUBMIT_ONGOING_REPORTING_SUCCESS: {
        const data = action.data as IOPSReporting
        if (!state || state?.id !== data.id) {
          return null
        }

        return {
          ...state,
          ...data,
          clientInfo: {
            ...(state.clientInfo || {}),
            ...(data.clientInfo || {}),
          } as IProspect,
        }
      }
      case actions.UPDATE_LOST_DEAL_NOTES_SUCCESS: {
        const data = action.data as Pick<IOPSReporting, 'id' | 'lostDealNotes'>
        // A save landing after the reader switched versions belongs to the other version.
        if (state?.id !== data.id) return state
        return { ...state, lostDealNotes: data.lostDealNotes }
      }
      case actions.UPDATE_FINANCIALS_DATES_SUCCESS: {
        const data = action.data as IOPSReportingFlow
        return {
          ...state,
          opsReportingFlows: (state.opsReportingFlows || []).map((item) =>
            item.id === data.id
              ? {
                  ...item,
                  ...data,
                }
              : item,
          ),
        }
      }
      case actions.UPDATE_REPORTING_FLOW_ADDITIONAL_SUCCESS: {
        const data = action.data as IOPSReportingFlow
        if (state.opsReportingFlows) {
          return {
            ...state,
            opsReportingFlows: state.opsReportingFlows.map((flow) =>
              flow.id === data.id ? data : flow,
            ),
          }
        }
        return state
      }
      case actions.UPDATE_REPORTING_REQUEST: {
        const params = action.params as
          { type?: OngoingReportingType; uncategorizedFilesMapping?: { id: string }[] } | undefined
        if (
          params?.type === OngoingReportingType.Uncategorized &&
          params?.uncategorizedFilesMapping?.length
        ) {
          const fileIDsToUpdate = params.uncategorizedFilesMapping.map(
            ({ id }: { id: string }) => id,
          )

          return {
            ...state,
            files: state.files?.filter(
              ({ type, id }) =>
                asOngoingReportingType(type) !== OngoingReportingType.Uncategorized ||
                !fileIDsToUpdate.includes(id),
            ),
          }
        }
        return state
      }
      case UPDATE_OPS_REPORTING: {
        const data = action.data as IOPSReporting
        if (!state || state?.id !== data.id) {
          return state
        }

        return {
          ...state,
          ...data,
          clientInfo: {
            ...(state.clientInfo || {}),
            ...(data.clientInfo || {}),
          } as IProspect,
        }
      }
      case actions.HIDE:
      case actions.LIST_SUCCESS:
        return null
      default:
        return state
    }
  },
  prospectsAggregation: createLoadingDataReducer<IProspectsAggregation>([actions.LIST_REQUEST]),
  pipelineMetrics: createLoadingDataReducer<IProspectPipelineMetrics>([
    actions.GET_PIPELINE_METRICS_REQUEST,
  ]),
  prospectReporting: createLoadingDataReducer<IProspectReportingAggregation>(
    [actions.LIST_PROSPECT_REPORTING_REQUEST],
    [],
    false,
    [actions.HIDE_PROSPECT_REPORTING],
  ),
  prospectTerms: createLoadingDataReducer<{ data: IProspectTerms[] }>(
    [actions.LIST_PROSPECT_TERMS_REQUEST],
    [
      actions.DELETE_PROSPECT_TERMS_REQUEST,
      actions.UPDATE_PROSPECT_TERMS_REQUEST,
      actions.CREATE_PROSPECT_TERMS_REQUEST,
    ],
    true,
  ),
  inventorySummaryInfo: createLoadingDataReducer<IProspectSummaryInventory>([
    actions.SHOW_INVENTORY_SUMMARY_INFO_REQUEST,
  ]),
  cashFlowSummaryInfo: createLoadingDataReducer<IProspectSummaryCashFlow>([
    actions.SHOW_CASH_FLOW_SUMMARY_INFO_REQUEST,
  ]),
  termStructureSummaryInfo: createLoadingDataReducer<IProspectTermsSummaryInfo>([
    actions.SHOW_TERM_STRUCTURE_SUMMARY_INFO_REQUEST,
  ]),
  arSummary: createLoadingDataReducer<IProspectARSummary>(
    [actions.LIST_AR_SUMMARY_REQUEST, actions.LIST_AR_DETAIL_REQUEST],
    [actions.UPDATE_AR_SUMMARY_REQUEST, actions.UPDATE_REPORTING_FLOW_ADDITIONAL_REQUEST],
  ),
  apSummary: createLoadingDataReducer<IProspectAPSummary>([
    actions.LIST_AP_SUMMARY_REQUEST,
    actions.LIST_AP_DETAIL_REQUEST,
  ]),
  overviewFeed: createLoadingDataReducer<IOverviewFeed>([actions.LOAD_OVERVIEW_FEED_REQUEST]),
  date(
    state: { AR: string; AP: string } = { AR: '', AP: '' },
    action: IAction,
  ): { AR: string; AP: string } {
    switch (action.type) {
      case actions.GET_DATE_AP_SUCCESS:
      case actions.UPDATE_DATE_AP_SUCCESS:
        return { ...state, AP: action.data as string }
      case actions.GET_DATE_AR_SUCCESS:
      case actions.UPDATE_DATE_AR_SUCCESS:
        return { ...state, AR: action.data as string }
      default:
        return state
    }
  },
  refreshCount: (state = 0, action: IAction) => {
    switch (action.type) {
      case actions.PROSPECT_REFRESH_COUNT:
        return state + 1
    }

    return state
  },
})
